import { DownOutlined } from '@ant-design/icons';
import { Avatar, Button, type ButtonProps, Typography } from 'antd';
import clx from 'classnames';
import { forwardRef } from 'react';
import styled, { useTheme } from 'styled-components';
import { getNameInitials } from '../../utils/getNameInitials.ts';

const { Text, Paragraph } = Typography;

const AvatarUser = styled(Avatar)`
  display: flex;
  flex: 0 0 38px;
  min-width: 0;
`;
const ArrowIcon = styled(DownOutlined)`
  flex: 0 0 auto;
  min-width: 0;
`;

const StyledUserButton = styled(Button)`
  display: flex;
  flex: 0 1 auto;
  min-width: 0;
  height: auto;
  padding: 4px;
  padding-right: 8px;
  border-radius: 23px;
  border: none;
  color: ${props => props.theme.colorText};
  opacity: 1;
  &.ant-btn-link:not(:disabled):not(.ant-btn-disabled) {
    color: ${props => props.theme.colorText};
    opacity: 1;
    &:active,
    &:hover {
      color: ${props => props.theme.colorPrimaryHover};
      background-color: ${props => props.theme.colorFillContent};
    }
  }
  .ant-avatar {
    font-family: ${props => props.theme.fontFamily};
    background-color: ${props => props.theme.colorPrimary};
  }
  &:hover {
    .ant-avatar {
      background-color: ${props => props.theme.colorPrimaryHover};
    }
  }

  &:not(:disabled):focus-visible {
    outline-offset: 0 !important;
  }

  &.is-dark-theme {
    color: white;
    &.ant-btn-link:not(:disabled):not(.ant-btn-disabled) {
      color: white;
    }
    .ant-typography {
      color: white;
    }
    .ant-avatar {
      background-color: white;
    }
    &:active,
    &:hover {
      background-color: ${props => props.theme.colorPrimaryActive};
      color: white;
      &.ant-btn-link:not(:disabled):not(.ant-btn-disabled) {
        background-color: ${props => props.theme.colorPrimaryActive};
      }
      .ant-typography {
        color: white;
      }
      .ant-avatar {
        background-color: white;
      }
    }
    // stili dark per Avatar
    ${AvatarUser} {
      background-color: white;
      color: ${props => props.theme.colorPrimaryActive};
      .ant-avatar-string {
        background-color: transparent;
      }
    }
    // Stili dark per ArrowIcon
    ${ArrowIcon} {
      color: white;
      opacity: 0.5;
    }
    // Stili dark all'hover del bottone
    &:hover {
      ${ArrowIcon} {
        color: white;
        opacity: 1;
      }
    }
  }
`;

const CompactStyledUserButton = styled(StyledUserButton)`
  padding: 4px;
  .ant-avatar {
    margin-right: 4px;
  }
`;

const ContentWrapper = styled.div`
  display: flex;
  min-width: 0;
  max-width: 100%;
  align-items: center;
`;

const UserTextWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 0 1 auto;
  min-width: 0;
  max-width: 270px;
  text-align: left;
  line-height: 1.3;
  padding: 0 8px;
`;
const UserNameLine = styled(Text)`
  display: inline-block;
  color: inherit;
  line-height: 1.3;
`;

const LineParagraph = styled(Paragraph)`
  font-weight: 400;
  font-size: 0.9em;
  line-height: 1.3;

  display: inline-block;
  flex: 0 1 auto;
  min-width: 0;
  margin-top: 0;
  color: inherit;
  &.ant-typography {
    margin-bottom: 0;
  }
`;
export interface IUserButtonBaseProps extends Omit<ButtonProps, 'type'> {
  compact?: boolean;
  name?: string;
  role?: string;
}

export const UserMenuAccessibilityClassName = 'user-menu-button--focus';

/**
 * Bottone personalizzato per il menu utente
 * Visualizza il nome, il ruolo dell'utente loggato.
 */
export const UserButtonBase = forwardRef<
  HTMLButtonElement,
  IUserButtonBaseProps
>(function UserButtonBase(props, ref) {
  const { compact, name, role, ...buttonProps } = props;
  const theme = useTheme();
  const initials = getNameInitials(name);

  const buttonClassName = clx(
    buttonProps.className,
    UserMenuAccessibilityClassName,
    {
      'is-dark-theme': theme.darkAdminHeaderFlag,
    },
  );

  if (compact)
    return (
      <CompactStyledUserButton
        ref={ref}
        aria-haspopup
        type="link"
        {...buttonProps}
        className={buttonClassName}
      >
        <ContentWrapper title={`${name ?? ''} - ${role ?? ''}`}>
          <AvatarUser aria-hidden size={38}>
            {initials}
          </AvatarUser>
          <ArrowIcon aria-hidden />
        </ContentWrapper>
      </CompactStyledUserButton>
    );

  return (
    <StyledUserButton
      ref={ref}
      aria-haspopup
      type="link"
      {...buttonProps}
      className={buttonClassName}
    >
      <ContentWrapper>
        <AvatarUser aria-hidden size={38}>
          {initials}
        </AvatarUser>
        <UserTextWrapper>
          <UserNameLine
            strong
            ellipsis
            title={`${name ?? ''}${role ? ' - ' + role : ''}`}
          >
            {name}
          </UserNameLine>
          {role && (
            <LineParagraph ellipsis title={role}>
              {role}
            </LineParagraph>
          )}
        </UserTextWrapper>
        <ArrowIcon aria-hidden />
      </ContentWrapper>
    </StyledUserButton>
  );
});

UserButtonBase.displayName = 'UserButtonBase';
